import {
    Box,
    Button,
    CloseButton,
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tr,
    VStack,
} from '@chakra-ui/react';
import type { ExtractionLayoutField, ExtractionObservation } from '../../hooks/invoice/useInvoiceExtractionLayout';

const ACCENT = '#0071E3';

export interface InvoiceExtractionHintsPanelProps {
    identifierFieldTitle: string;
    vendorName: string;
    invoiceTypeLabel: string;
    includePurchaseOrder: boolean;
    observations: ExtractionObservation[];
    isLoading: boolean;
    loadFailed?: boolean;
    onRetryLoad?: () => void;
    isSaving: boolean;
    canEdit: boolean;
    onBack: () => void;
    onSaveHint: (observationId: string, enhancedHint: string) => void;
    onDeleteObservation: (observationId: string) => void;
    draftHints: Record<string, string>;
    onDraftHintChange: (observationId: string, value: string) => void;
}

function fieldLabel(
    field: ExtractionObservation['field'],
    identifierFieldTitle: string,
    includePurchaseOrder: boolean,
): string {
    if (field === 'invoiceNumber') return 'Invoice #';
    if (field === 'purchaseOrderNumber') return 'PO #';
    if (field === 'shopRoNumber') return 'Shop RO #';
    if (field === 'roNumber') return includePurchaseOrder ? 'RO #' : identifierFieldTitle;
    if (field === 'vendor') return 'Vendor';
    if (field === 'invoiceDate') return 'Invoice date';
    if (field === 'taxesTotal') return 'Taxes';
    if (field === 'orderTotal') return 'Grand total';
    return field;
}

function formatWhen(at: string): string {
    const parsed = new Date(at);
    if (Number.isNaN(parsed.getTime())) return at;
    return parsed.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

export default function InvoiceExtractionHintsPanel({
    identifierFieldTitle,
    vendorName,
    invoiceTypeLabel,
    includePurchaseOrder,
    observations,
    isLoading,
    loadFailed,
    onRetryLoad,
    isSaving,
    canEdit,
    onBack,
    onSaveHint,
    onDeleteObservation,
    draftHints,
    onDraftHintChange,
}: InvoiceExtractionHintsPanelProps) {
    return (
        <VStack align="stretch" spacing={4} h="full" minH={0}>
            <Flex align="flex-start" justify="space-between" gap={3} flexShrink={0}>
                <Box minW={0}>
                    <Text
                        id="invoice-extraction-hints-title"
                        fontSize="lg"
                        fontWeight="bold"
                        color="gray.900"
                    >
                        AI extraction hints
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                        Saved corrections for {vendorName} {invoiceTypeLabel} invoices
                    </Text>
                </Box>
                <CloseButton
                    aria-label="Close AI extraction hints"
                    onClick={onBack}
                    isDisabled={isSaving}
                />
            </Flex>

            {isLoading ? (
                <Text color="gray.500">Loading saved hints…</Text>
            ) : loadFailed ? (
                <VStack align="start" spacing={2}>
                    <Text color="red.600">Could not load extraction hints.</Text>
                    {onRetryLoad ? (
                        <Button size="sm" variant="outline" onClick={onRetryLoad}>
                            Retry
                        </Button>
                    ) : null}
                </VStack>
            ) : observations.length === 0 ? (
                <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={6} bg="gray.50">
                    <Text color="gray.700" fontWeight="medium" mb={1}>
                        No saved hints yet
                    </Text>
                    <Text color="gray.600" fontSize="sm">
                        When you correct a value and choose &quot;Remember this&quot;, PartBay saves an AI hint here for future invoices from this vendor.
                    </Text>
                </Box>
            ) : (
                <Box overflow="auto" flex="1" minH={0} borderWidth="1px" borderColor="gray.200" borderRadius="md">
                    <Table size="sm" variant="simple">
                        <Thead bg="gray.50">
                            <Tr>
                                <Th>Field</Th>
                                <Th>Was extracted</Th>
                                <Th>Corrected to</Th>
                                <Th minW="280px">AI hint</Th>
                                <Th>When</Th>
                                <Th w="48px" />
                            </Tr>
                        </Thead>
                        <Tbody>
                            {observations.map((obs) => {
                                const draft = draftHints[obs.id] ?? obs.enhancedHint;
                                const dirty = draft.trim() !== obs.enhancedHint.trim();
                                return (
                                    <Tr key={obs.id}>
                                        <Td whiteSpace="nowrap">
                                            {fieldLabel(obs.field as ExtractionLayoutField, identifierFieldTitle, includePurchaseOrder)}
                                        </Td>
                                        <Td>{obs.extracted || '—'}</Td>
                                        <Td fontWeight="medium">{obs.corrected}</Td>
                                        <Td>
                                            <Textarea
                                                size="sm"
                                                value={draft}
                                                onChange={(e) => onDraftHintChange(obs.id, e.target.value)}
                                                isDisabled={!canEdit || isSaving}
                                                minH="88px"
                                                resize="vertical"
                                                placeholder="Hint for where to find this value"
                                            />
                                            {dirty && canEdit ? (
                                                <Button
                                                    mt={2}
                                                    size="xs"
                                                    bg={ACCENT}
                                                    color="white"
                                                    _hover={{ bg: '#3544a0' }}
                                                    isLoading={isSaving}
                                                    onClick={() => onSaveHint(obs.id, draft.trim())}
                                                >
                                                    Save hint
                                                </Button>
                                            ) : null}
                                        </Td>
                                        <Td whiteSpace="nowrap" fontSize="xs" color="gray.600">
                                            {formatWhen(obs.at)}
                                        </Td>
                                        <Td>
                                            <Button
                                                size="xs"
                                                variant="ghost"
                                                colorScheme="red"
                                                isDisabled={!canEdit || isSaving}
                                                onClick={() => onDeleteObservation(obs.id)}
                                            >
                                                Delete
                                            </Button>
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            )}
        </VStack>
    );
}
